<template>
  <mi-header class="mi-app-header">
    <!-- Areas menu (hamburger) -->
    <mi-btn
      class="mi-app-header__areas-btn"
      icon-type
      dense
      flat
      aria-label="Open areas menu"
    >
      <img src="@/assets/images/header-menu.svg" alt="" />
      <mi-menu
        class="mi-app-header__areas-menu"
        :offset="[0, 6]"
        anchor="bottom left"
        self="top left"
        auto-close
      >
        <mi-list>
          <mi-list-item
            v-for="({ title, icon }, name) in areasMenuItems"
            :key="name"
            :to="{ name }"
            class="mi-app-header__areas-item text-weight-regular"
            clickable
          >
            <mi-list-item-section class="items-center q-pr-none q-mr-md" avatar>
              <img :src="icon" alt="icon" width="30" height="30" />
            </mi-list-item-section>
            <mi-list-item-section>
              {{ title }}
            </mi-list-item-section>
          </mi-list-item>
        </mi-list>
      </mi-menu>
    </mi-btn>

    <div class="mi-app-header__nav flex items-center no-wrap">
      <!-- Application feature title -->
      <span class="text-uppercase text-weight-bold text-family-condensed"> {{ currentAppFeatures.title }} </span>

      <!-- Feature children routes (inline main navigation) -->
      <template v-if="currentFeatureSubRoutes.length">
        <mi-separator class="mi-app-header__nav-separator" vertical></mi-separator>
        <router-link
          v-for="{ name, title } in currentFeatureSubRoutes"
          :key="name"
          :to="{ name }"
          class="mi-app-header__nav-link text-weight-regular"
          active-class="mi-app-header__nav-link--active"
        >
          {{ title }}
        </router-link>
      </template>
    </div>

    <div class="mi-app-header__badge">
      <q-badge
        v-if="isDemoEnvironment"
        class="mi-app-header__badge--message text-uppercase q-mr-md q-px-md"
        color="accent"
        rounded
      >
        Demo environment
      </q-badge>
      <q-badge
        v-else-if="hasSubstringDashInt"
        class="mi-app-header__badge--message text-uppercase q-mr-md q-px-md"
        color="accent"
        rounded
      >
        Integration environment
      </q-badge>
    </div>

    <div class="mi-app-header__activity-center-wrapper">
      <mi-btn
        class="mi-app-header__actions--activity-center"
        fab
        icon-type
        dense
        @click="toggleActivityCenter"
      >
        <img
          v-if="unreadCount > 0"
          src="@/assets/images/notifications/ic_notification.svg"
          alt="activity center"
          width="24"
          height="24"
        />
        <img
          v-else
          src="@/assets/images/notifications/ic_notification_empty.svg"
          alt="activity center"
          width="24"
          height="24"
        />
      </mi-btn>
    </div>

    <!-- User info -->
    <template v-if="user.username" #items-right>
      <mi-btn
        class="user-menu-btn"
        flat
        dense
        icon-right="caret-down"
      >
        {{ user.username }}
        <mi-menu
          class="mi-app-header__user-menu"
          :offset="[0, 4]"
          anchor="bottom right"
          self="top right"
          auto-close
        >
          <mi-list>
            <!-- What's New (User's View) - visible to everyone -->
            <mi-list-item
              class="mi-app-header__user-menu-item"
              clickable
              @click="openWhatsNew(INFO_TAB_NAMES.USER)"
            >
              <mi-list-item-section avatar>
                <img
                  src="@/assets/images/whats-new-icon-red.svg"
                  alt=""
                  width="24"
                  height="24"
                />
              </mi-list-item-section>
              <mi-list-item-section>
                <mi-list-item-label class="text-weight-regular">
                  What's New
                </mi-list-item-label>
              </mi-list-item-section>
            </mi-list-item>

            <!-- Menu options -->
            <mi-list-item
              v-for="({ title, path, icon }) in USER_MENU_OPTIONS"
              v-show="userAccess(title)"
              :key="path"
              class="mi-app-header__user-menu-item"
              clickable
              :to="path"
            >
              <mi-list-item-section avatar>
                <mi-icon :name="icon" color="accent" size="24px"></mi-icon>
              </mi-list-item-section>
              <mi-list-item-section>
                <mi-list-item-label
                  class="text-weight-regular"
                >
                  {{ title }}
                </mi-list-item-label>
              </mi-list-item-section>
            </mi-list-item>

            <!-- Add New Update (same visibility condition as the original What's New admin tab) -->
            <template v-if="canAddNewUpdate">
              <mi-separator></mi-separator>
              <mi-list-item
                class="mi-app-header__user-menu-item"
                clickable
                @click="openWhatsNew(INFO_TAB_NAMES.ADMIN)"
              >
                <mi-list-item-section avatar>
                  <mi-icon name="add-new" color="accent" size="24px"></mi-icon>
                </mi-list-item-section>
                <mi-list-item-section>
                  <mi-list-item-label class="text-weight-regular">
                    Add New Update
                  </mi-list-item-label>
                </mi-list-item-section>
              </mi-list-item>
            </template>
          </mi-list>
        </mi-menu>
      </mi-btn>
    </template>
  </mi-header>
  <div v-if="showWhatsNew" class="whats-new-wrapper mi-drawer-content">
    <div class="drawer-background"></div>
    <mi-drawer
      v-model="showWhatsNew"
      overlay
      bordered
      side="right"
      width="800"
    >
      <q-scroll-area class="fit">
        <whats-new :initial-tab="whatsNewTab" @open:whats-new="closeWhatsNew"></whats-new>
      </q-scroll-area>
    </mi-drawer>
  </div>

  <activity-center
    v-if="showActivityCenter"
    :notification-id="activityCenterNotificationId"
    @close="toggleActivityCenter"
    @notifications-loaded="handleActivityCenterNotificationsLoaded"
  ></activity-center>

  <check-whats-new-modal v-if="showWhatsNewModal"></check-whats-new-modal>
</template>

<script>
  import { useStore } from 'vuex'
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { useNotificationsState } from '@/composables/useNotificationsState'
  import {
    APP_FEATURES_ROUTES,
    ACTIVE_APP_FEATURES_ROUTES,
    HOME_ROUTE, INTERNAL_FEATURES_ROUTES,
    USER_MANAGEMENT_ROUTE
  } from '@/router/routeNames'
  import { canAccess } from '@/utils/accessCheck'
  import WhatsNew from '@/views/whats-new/WhatsNew.vue'
  import { NOTIFICATION_TYPES } from '@/constants/notifications'
  import { INFO_TAB_NAMES } from '@/constants/whatsNew'
  import CheckWhatsNewModal from '@/views/CheckWhatsNew.vue'
  import ActivityCenter from '@/components/activity-center/ActivityCenter.vue'
  import { USER_MENU_OPTIONS } from '@/constants/appHeader'
  import { readAllNotifications } from '@/api/rest/activity-center'

  export default {
    name: 'AppHeader',
    components: { CheckWhatsNewModal, WhatsNew, ActivityCenter },
    props: {
      activityCenterRequest: {
        type: Object,
        default: null
      }
    },
    setup(props) {
      const showWhatsNew = ref(false)
      const whatsNewTab = ref(INFO_TAB_NAMES.USER)
      const areasMenuItems = reactive({
        [HOME_ROUTE.name]: { title: HOME_ROUTE.title, icon: HOME_ROUTE.icon },
        ...ACTIVE_APP_FEATURES_ROUTES
      })
      const showActivityCenter = ref(false)
      const activityCenterNotificationId = ref(null)
      const activeFeatureSubRoute = ref({})

      const route = useRoute()

      const store = useStore()
      const user = computed(() => store.state.user)

      const unreadCount = computed(
        () => store.getters[
          'activity-center/unreadCount'
        ] || 0
      )

      const {
        notificationsState,
        fetchNotificationsState
      } = useNotificationsState()

      const showWhatsNewModal = computed(() => notificationsState.newModalNotificationAvailable
        && !process.env.VUE_APP_E2E_ENABLED)

      const fetchNotifications = async type => {
        await fetchNotificationsState({
          type,
          seen: false
        })
      }

      const whatsNewChecker = async () => {
        await fetchNotifications(NOTIFICATION_TYPES.whatsNew)
        await fetchNotifications(NOTIFICATION_TYPES.whatsNewModal)
      }

      const openWhatsNew = (tab = INFO_TAB_NAMES.USER) => {
        whatsNewTab.value = tab
        showWhatsNew.value = true
      }

      const closeWhatsNew = () => {
        showWhatsNew.value = false
      }

      const toggleActivityCenter = () => {
        if (showActivityCenter.value) {
          showActivityCenter.value = false
          return
        }

        activityCenterNotificationId.value = null
        showActivityCenter.value = true
      }

      const handleActivityCenterNotificationsLoaded = async () => {
        if (
          activityCenterNotificationId.value
          || unreadCount.value === 0
        ) {
          return
        }

        try {
          await readAllNotifications()
          store.commit('activity-center/RESET_UNREAD_COUNT')
        }
        catch (error) {
          console.error(
            'Failed to mark all notifications as read:',
            error
          )
        }
      }

      watch(
        () => props.activityCenterRequest,
        request => {
          if (!request?.eventId) return

          activityCenterNotificationId.value = request.eventId
          showActivityCenter.value = true
        }
      )

      const userAccess = title => (title === USER_MANAGEMENT_ROUTE.title
        ? canAccess('LIST_USERS') && canAccess('CUD_USERS')
        : true)

      const canAddNewUpdate = computed(() => {
        if (process.env.VUE_APP_E2E_ENABLED) return true
        if (!canAccess('ADMIN')) return false

        return user.value.username === 'admin'
          ? canAccess('CREATE_VALIDATION_SETTINGS')
          : canAccess('CREATE_NOT_USABLE_CHOICES_SETTINGS')
      })

      const currentAppFeatures = computed(() => APP_FEATURES_ROUTES[route.matched[0]?.name]
        || INTERNAL_FEATURES_ROUTES[route.matched[0]?.name]
        || {})

      const currentFeatureSubRoutes = computed(() => currentAppFeatures.value.children || [])

      const hasSubstringDashInt = computed(() => {
        const regex = /-int/
        const domainURL = process.env.VUE_APP_DOMAIN_URL
        return regex.test(domainURL)
      })

      const isDemoEnvironment = computed(() => process.env.BASE_URL === process.env.VUE_APP_DEMO_ENV_PREFIX)

      onMounted(() => {
        watch(
          () => route.name,
          () => whatsNewChecker(),
          { immediate: true }
        )
      })

      return {
        USER_MENU_OPTIONS,
        INFO_TAB_NAMES,
        canAddNewUpdate,
        user,
        openWhatsNew,
        closeWhatsNew,
        unreadCount,
        toggleActivityCenter,
        handleActivityCenterNotificationsLoaded,
        userAccess,
        currentFeatureSubRoutes,
        hasSubstringDashInt,
        isDemoEnvironment,
        areasMenuItems,
        showWhatsNew,
        whatsNewTab,
        showActivityCenter,
        activityCenterNotificationId,
        activeFeatureSubRoute,
        currentAppFeatures,
        showWhatsNewModal
      }
    }
  }
</script>

<style lang="scss">
  .mi-drawer-content {
    .q-drawer {
      /* stylelint-disable declaration-no-important */
      width: 41% !important;
      top: 0 !important;
    }
  }

  @media (max-width: $mi-responsive-medium-width) {
    .mi-drawer-content {
      .q-drawer {
        width: 100vw !important;
      }
    }
  }

  .drawer-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(67, 65, 65, .6);
    z-index: 2998;
  }

  .q-menu.mi-app-header__areas-menu {
    .q-item + .q-item:before {
      background: none;
    }

    .mi-app-header__areas-item:hover {
      background-color: $mi-red-50;
    }

    .mi-app-header__areas-item .q-focus-helper {
      display: none;
    }
  }

  .q-menu.mi-app-header__user-menu {
    .q-item + .q-item:before {
      background: none;
    }
  }
</style>

<style lang="scss" scoped>
  .mi-app-header {
    &__areas-btn {
      align-self: center;
      margin-right: 1rem;

      ::v-deep(.q-btn__content) {
        padding: 10px;
        border-radius: 12px;
        background-color: $mi-silver-100;
      }

      ::v-deep(.q-focus-helper),
      ::v-deep(.q-ripple) {
        display: none !important;
      }

      &.mi-btn.q-btn.q-btn--flat:hover,
      &.mi-btn.q-btn.q-btn--flat:focus {
        background-color: transparent !important;
      }

      &:hover {
        cursor: pointer;

        ::v-deep(.q-btn__content) img {
          /* Recolor the menu icon to $mi-red-500 (#e40045) on hover */
          filter: brightness(0) saturate(100%) invert(16%) sepia(97%)
            saturate(5884%) hue-rotate(329deg) brightness(94%) contrast(104%);
        }
      }
    }

    &__areas-item ::v-deep(.q-item__section--avatar) {
      width: 24px;
      height: 24px;
      margin-right: .75rem;
      border-radius: 10px;
    }

    &__nav {
      align-self: center;

      .mi-app-header__nav-link {
        padding: 4px 12px;
        margin-right: .5rem;
        border-radius: 8px;
        font-weight: 400;
        color: $mi-anthracite-600;
        text-decoration: none;

        &:last-of-type {
          margin-right: 0;
        }

        &:hover,
        &:focus,
        &:active {
          text-decoration: none;
        }

        &:hover,
        &:focus-visible {
          color: $mi-anthracite-800;
          background-color: $mi-silver-100;
        }

        &.mi-app-header__nav-link--active,
        &.router-link-active {
          color: $mi-anthracite-800;
          font-weight: 600;
          background-color: $mi-silver-200;
        }
      }
    }

    &__nav-separator {
      height: 24px;
      margin: 0 1rem;
    }

    &__badge {
      display: flex;
      justify-content: center;
      flex-grow: 1;
      margin-left: 14px;

      &--message {
        font-size: $mi-field-font-size;
        padding: .75rem;
        font-family: $mi-typography-font-family-condensed;
        font-weight: 600;
      }
    }

    ::v-deep(.mi-header__items--right) {
      align-items: center;

      &:before {
        top: 50%;
        bottom: auto;
        height: 24px;
        transform: translateY(-50%);
      }
    }

    &__activity-center-wrapper {
      position: relative;
      display: inline-flex;
      align-self: center;
      align-items: center;
      justify-content: center;
    }

    &__activity-center-badge {
      position: absolute;
      top: -4px;
      right: -4px;
      min-width: 18px;
      height: 18px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0 5px;
      font-size: 11px;
      line-height: 18px;
      pointer-events: none;
      z-index: 1;
    }

    &__actions {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: center;

      > * {
        align-self: center;
      }

      &--notification:hover {
        cursor: pointer;
      }

      &--activity-center,
      &--notification,
      &--supermenu-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      &--activity-center img {
        width: 24px;
        height: 24px;
        display: block;
      }

      &--notification img {
        width: 24px;
        height: 21px;
        display: block;
      }

      &--supermenu-btn img {
        width: 18px;
        height: 18px;
        display: block;
      }
    }
  }

  .user-menu-btn.mi-btn {
    align-self: center;
  }

  .user-menu-btn ::v-deep(.q-icon) {
    font-size: .75em;
  }

  .user-menu-btn.mi-btn ::v-deep(.q-focus-helper) {
    display: none !important;
  }

  .user-menu-btn.mi-btn.q-btn.q-btn--flat:hover,
  .user-menu-btn.mi-btn.q-btn.q-btn--flat:focus {
    background-color: transparent !important;
    color: $mi-anthracite-800 !important;
  }

  .whats-new-wrapper {
    z-index: 9999;
  }
</style>
